/**
 * Base class for every Configuration tab view-model.
 *
 * Contract: the React `Configuration` component is the single state owner.
 * Each tab's render slice lives in `component.state.tabs[sliceKey]`;
 * controllers read it through `this.state` and mutate it exclusively via
 * the component's real `setState`, which gives batching, async semantics,
 * and full React DevTools visibility.
 *
 * Cross-tab effects (e.g. segmentation fields invalidating balance rules)
 * are handled by passing computed values as explicit arguments to the
 * receiving controller — no in-flight sibling-state reading needed.
 *
 * Snapshots (change-detection baselines) are intentionally NOT React
 * state: they never drive a render, so they stay as plain instance fields
 * on the subclasses.
 */
class TabController {
    /**
     * @param {import('react').Component} page     host component (state owner)
     * @param {string}                   sliceKey  key under `page.state.tabs`
     */
    constructor(page, sliceKey) {
        this.page = page;
        this.sliceKey = sliceKey;
    }

    /** Live read from the host component state — no local copy needed. */
    get state() {
        return this.page.state.tabs[this.sliceKey];
    }

    /**
     * Patches this tab's slice through the host's real `setState`.
     *
     * `partial` may be a plain object (shallow-merged into the slice) or an
     * updater `(prevSlice) => partialOrNull`. Returning a falsy value bails
     * out without scheduling a render, matching React's own bail-out
     * convention.
     */
    setState(partial) {
        this.page.setState((prev) => {
            const prevSlice = prev.tabs[this.sliceKey];
            const next = typeof partial === 'function' ? partial(prevSlice) : partial;
            if (!next) return null;
            return {
                tabs: { ...prev.tabs, [this.sliceKey]: { ...prevSlice, ...next } }
            };
        });
    }

    /**
     * Replaces this tab's slice wholesale (cancel flows that revert to a
     * snapshot). Goes through the same host `setState` queue.
     */
    replaceState(nextSlice) {
        this.page.setState((prev) => ({
            tabs: { ...prev.tabs, [this.sliceKey]: nextSlice }
        }));
    }
}

export default TabController;
